import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { api } from "../lib/api";

const offerTypeLabels = {
  gift: 'Presente', invitation: 'Convite', benefit: 'Benefício', sponsorship: 'Patrocínio', other: 'Outro'
};

const OffersPage = () => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showApprovalForm, setShowApprovalForm] = useState(null);
  const [showRecoveryForm, setShowRecoveryForm] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [analyzingRgpc, setAnalyzingRgpc] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [tenantSettings, setTenantSettings] = useState(null);

  const canAnalyze = ['super_admin', 'tenant_admin', 'compliance_officer'].includes(user?.role);
  const canApprove = ['super_admin', 'tenant_admin', 'compliance_officer'].includes(user?.role);
  const canEdit = ['super_admin', 'tenant_admin'].includes(user?.role);
  const canDelete = ['super_admin', 'tenant_admin'].includes(user?.role);

  const fetchOffers = async () => {
    try {
      const [offersRes, employeesRes] = await Promise.all([api.get('/offers'), api.get('/employees')]);
      setOffers(offersRes.data);
      setEmployees(employeesRes.data);
      
      // Fetch tenant settings for policy display
      try {
        const tenantRes = await api.get('/auth/me');
        if (tenantRes.data.tenant_id) {
          const settingsRes = await api.get(`/tenants/${tenantRes.data.tenant_id}`);
          setTenantSettings(settingsRes.data?.offer_settings || {});
        }
      } catch (e) {
        console.log('Could not fetch tenant settings');
      }
    } catch (error) {
      toast.error('Erro ao carregar ofertas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const runRgpcAnalysis = async () => {
    setAnalyzingRgpc(true);
    // Show start notification
    toast.info('Análise RGPC iniciada... A processar ofertas.', { duration: 2000 });
    try {
      const response = await api.post('/compliance/analyze-all-offers');
      const data = response.data;
      
      // Show completion toast with details
      let message = `Análise concluída: ${data.total_analyzed} ofertas analisadas`;
      if (data.non_compliant > 0) {
        message += ` • ${data.compliant} conformes • ${data.non_compliant} não conformes`;
      } else {
        message += ` • Todas conformes!`;
      }
      if (data.critical > 0) {
        toast.warning(message + ` • ${data.critical} críticas!`, { duration: 6000 });
      } else if (data.non_compliant > 0) {
        toast.info(message, { duration: 5000 });
      } else {
        toast.success(message, { duration: 4000 });
      }
      
      // Show notification about new conflicts detected
      if (data.new_conflicts_detected > 0) {
        toast.warning(`⚠ ${data.new_conflicts_detected} novo(s) conflito(s) RGPC detetado(s)! Verifique as notificações.`, { duration: 6000 });
      }
      
      fetchOffers();
    } catch (error) {
      console.error('RGPC analysis error:', error);
      toast.error('Erro na análise RGPC');
    } finally {
      setAnalyzingRgpc(false);
    }
  };

  const downloadRgpcReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await api.get('/reports/risk/consolidated/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_rgpc_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      toast.success('Relatório descarregado');
    } catch (error) {
      console.error('Report download error:', error);
      toast.error('Erro ao descarregar relatório');
    } finally {
      setDownloadingReport(false);
    }
  };

  const getStatusBadge = (offer) => {
    if (offer.status === 'ipss_donated') return { class: 'bg-purple-500 text-white', text: 'Doada IPSS' };
    if (offer.status === 'lottery') return { class: 'bg-indigo-500 text-white', text: 'Sorteio' };
    if (offer.status === 'recovered') return { class: 'bg-purple-500 text-white', text: 'Recuperada' };
    if (offer.status === 'auto_rejected') return { class: 'bg-red-600 text-white', text: 'Rejeitada Auto.' };
    if (offer.status === 'approved') return { class: 'status-badge-approved', text: 'Aprovada' };
    if (offer.status === 'rejected') return { class: 'status-badge-rejected', text: 'Rejeitada' };
    if (offer.status === 'pending_approval') return { class: 'bg-amber-500 text-white', text: 'Aguarda Aprovação' };
    if (offer.status === 'pending') {
      if (offer.exceeds_limit) return { class: 'bg-orange-500 text-white', text: 'Excede Limite' };
      if (offer.approval_required) return { class: 'status-badge-pending', text: 'Pendente' };
      return { class: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400', text: 'Auto-Aprovada' };
    }
    return { class: 'status-badge-pending', text: offer.status };
  };

  // Helper function to get offer value (supports both value and estimated_value)
  // Converts to number to handle cases where value might be stored as string
  const getOfferValue = (offer) => {
    const val = offer.value ?? offer.estimated_value ?? 0;
    return typeof val === 'string' ? parseFloat(val) || 0 : val;
  };
  
  // Helper function to get offered by info
  const getOfferedBy = (offer) => offer.offered_by || offer.related_entity_name || '-';
  
  // Check if offer needs approval action
  const needsApproval = (offer) => ['pending', 'pending_approval'].includes(offer.status);

  const handleDeleteOffer = async (offerId) => {
    if (!confirm('Tem certeza que deseja eliminar esta oferta? Esta ação é irreversível.')) return;
    try {
      await api.delete(`/offers/${offerId}`);
      toast.success('Oferta eliminada com sucesso');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar oferta');
    }
  };

  const getRgpcBadge = (offer) => {
    const analysis = offer.rgpc_analysis;
    if (!analysis) return null;
    if (analysis.is_compliant) return { class: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700', text: '✓ RGPC' };
    if (analysis.rgpc_score > 0.6) return { class: 'bg-red-100 dark:bg-red-900/30 text-red-700', text: '⚠ RGPC Crítico' };
    return { class: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700', text: '⚠ RGPC' };
  };

  return (
    <div data-testid="offers-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Registo de Ofertas</h1>
          <p className="text-muted-foreground">Gestão de ofertas conforme RGPC</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canAnalyze && (
            <>
              <button onClick={runRgpcAnalysis} disabled={analyzingRgpc} className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2" data-testid="analyze-rgpc-btn">
                {analyzingRgpc ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>A analisar...</> : <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Analisar RGPC
                </>}
              </button>
              <button onClick={downloadRgpcReport} disabled={downloadingReport} className="px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="download-rgpc-report-btn">
                {downloadingReport ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div> : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                Relatório RGPC
              </button>
            </>
          )}
          <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2" data-testid="create-offer-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Registar Oferta
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Total</p><p className="text-2xl font-bold">{offers.length}</p></div>
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Pendentes</p><p className="text-2xl font-bold text-amber-500">{offers.filter(o => needsApproval(o)).length}</p></div>
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Excedem Limite</p><p className="text-2xl font-bold text-orange-500">{offers.filter(o => o.exceeds_limit).length}</p></div>
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Rejeitadas Auto.</p><p className="text-2xl font-bold text-red-500">{offers.filter(o => o.status === 'auto_rejected').length}</p></div>
      </div>

      {/* RGPC Policy Info */}
      {tenantSettings && (
        <div className="rounded-xl border bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300">Política de Ofertas e Hospitalidades (RGPC)</h3>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                  <p className="text-muted-foreground">Limite de Aceitação</p>
                  <p className="text-lg font-bold text-emerald-600">€{tenantSettings.max_offer_value || 50}</p>
                  <p className="text-xs text-muted-foreground">Ofertas abaixo são auto-aprovadas</p>
                </div>
                <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                  <p className="text-muted-foreground">Requer Aprovação</p>
                  <p className="text-lg font-bold text-amber-600">&gt; €{tenantSettings.offer_approval_required_above || 25}</p>
                  <p className="text-xs text-muted-foreground">Requer aprovação de Compliance</p>
                </div>
                <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                  <p className="text-muted-foreground">Rejeição Automática</p>
                  <p className="text-lg font-bold text-red-600">&gt; €{tenantSettings.auto_reject_offers_above || 500}</p>
                  <p className="text-xs text-muted-foreground">Ofertas acima são auto-rejeitadas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : offers.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
          <p className="text-muted-foreground">Nenhuma oferta registada</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr><th>Colaborador</th><th>Tipo</th><th>Valor</th><th>Oferecido por</th><th>Status</th><th>RGPC</th><th></th></tr>
            </thead>
            <tbody>
              {offers.map((offer) => {
                const statusBadge = getStatusBadge(offer);
                const rgpcBadge = getRgpcBadge(offer);
                const offerValue = getOfferValue(offer);
                const offeredBy = getOfferedBy(offer);
                return (
                  <tr key={offer.offer_id} data-testid={`offer-row-${offer.offer_id}`}>
                    <td className="font-medium">{offer.employee_name}</td>
                    <td>
                      <div className="flex flex-col">
                        <span>{offerTypeLabels[offer.offer_type] || offer.offer_type}</span>
                        {offer.direction && <span className="text-xs text-muted-foreground">{offer.direction === 'received' ? 'Recebida' : 'Dada'}</span>}
                      </div>
                    </td>
                    <td className="font-semibold">{offerValue?.toFixed(2)} {offer.currency || 'EUR'}</td>
                    <td className="text-muted-foreground">{offeredBy}</td>
                    <td><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.class}`}>{statusBadge.text}</span></td>
                    <td>{rgpcBadge && <span className={`px-2 py-1 rounded-full text-xs font-medium ${rgpcBadge.class}`}>{rgpcBadge.text}</span>}</td>
                    <td>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => setShowDetailModal(offer)} 
                          className="p-1.5 rounded hover:bg-muted text-slate-600 dark:text-slate-400"
                          title="Ver Detalhes"
                          data-testid={`view-offer-${offer.offer_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        {canEdit && (
                          <button 
                            onClick={() => setShowEditModal(offer)} 
                            className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                            title="Editar"
                            data-testid={`edit-offer-${offer.offer_id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                        )}
                        {canDelete && (
                          <button 
                            onClick={() => handleDeleteOffer(offer.offer_id)} 
                            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                            title="Eliminar"
                            data-testid={`delete-offer-${offer.offer_id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                        {needsApproval(offer) && canApprove && (
                          <button 
                            onClick={() => setShowApprovalForm(offer)} 
                            className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                            title="Aprovar/Rejeitar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </button>
                        )}
                        {offer.status === 'auto_rejected' && canApprove && (
                          <button 
                            onClick={() => setShowRecoveryForm(offer)} 
                            className="p-1.5 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600"
                            title="Recuperar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && <CreateOfferModal employees={employees} onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); fetchOffers(); }} />}
      {showEditModal && <EditOfferModal offer={showEditModal} employees={employees} onClose={() => setShowEditModal(null)} onUpdated={() => { setShowEditModal(null); fetchOffers(); }} />}
      {showApprovalForm && <ApprovalModal offer={showApprovalForm} onClose={() => setShowApprovalForm(null)} onComplete={() => { setShowApprovalForm(null); fetchOffers(); }} />}
      {showRecoveryForm && <RecoveryModal offer={showRecoveryForm} onClose={() => setShowRecoveryForm(null)} onComplete={() => { setShowRecoveryForm(null); fetchOffers(); }} />}
      {showDetailModal && <OfferDetailModal offer={showDetailModal} tenantSettings={tenantSettings} canApprove={canApprove} onClose={() => setShowDetailModal(null)} onApprove={() => { setShowDetailModal(null); setShowApprovalForm(showDetailModal); }} onRecover={() => { setShowDetailModal(null); setShowRecoveryForm(showDetailModal); }} />}
    </div>
  );
};

// Create Offer Modal
const CreateOfferModal = ({ employees, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ employee_id: '', offer_type: 'gift', description: '', value: '', currency: 'EUR', offered_by: '', offer_date: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/offers', { ...formData, value: parseFloat(formData.value) });
      toast.success('Oferta registada com sucesso');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar oferta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Registar Oferta</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Colaborador *</label>
            <select required value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="">Selecionar...</option>
              {employees.map((emp) => <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo *</label>
              <select required value={formData.offer_type} onChange={(e) => setFormData({ ...formData, offer_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="gift">Presente</option>
                <option value="invitation">Convite</option>
                <option value="benefit">Benefício</option>
                <option value="sponsorship">Patrocínio</option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valor *</label>
              <div className="flex gap-2">
                <input type="number" step="0.01" required value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border border-input bg-background" placeholder="0.00" />
                <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="w-20 px-2 py-2 rounded-lg border border-input bg-background">
                  <option>EUR</option><option>USD</option><option>GBP</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Oferecido por</label>
              <input type="text" value={formData.offered_by} onChange={(e) => setFormData({ ...formData, offered_by: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data</label>
              <input type="date" value={formData.offer_date} onChange={(e) => setFormData({ ...formData, offer_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50">{loading ? 'A guardar...' : 'Registar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Offer Modal
const EditOfferModal = ({ offer, employees, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    offer_type: offer.offer_type || 'gift',
    description: offer.description || '',
    value: offer.value ?? offer.estimated_value ?? '',
    currency: offer.currency || 'EUR',
    offered_by: offer.offered_by || '',
    offer_date: offer.offer_date || '',
    status: offer.status || 'pending',
    notes: offer.notes || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/offers/${offer.offer_id}`, { 
        ...formData, 
        value: parseFloat(formData.value) || 0 
      });
      toast.success('Oferta atualizada com sucesso');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar oferta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Editar Oferta</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm"><strong>Colaborador:</strong> {offer.employee_name}</p>
            <p className="text-sm text-muted-foreground"><strong>ID:</strong> {offer.offer_id}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <select value={formData.offer_type} onChange={(e) => setFormData({ ...formData, offer_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="gift">Presente</option>
                <option value="invitation">Convite</option>
                <option value="benefit">Benefício</option>
                <option value="sponsorship">Patrocínio</option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valor</label>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border border-input bg-background" placeholder="0.00" />
                <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="w-20 px-2 py-2 rounded-lg border border-input bg-background">
                  <option>EUR</option><option>USD</option><option>GBP</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Oferecido por</label>
              <input type="text" value={formData.offered_by} onChange={(e) => setFormData({ ...formData, offered_by: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data</label>
              <input type="date" value={formData.offer_date} onChange={(e) => setFormData({ ...formData, offer_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="pending">Pendente</option>
              <option value="pending_approval">Aguarda Aprovação</option>
              <option value="approved">Aprovada</option>
              <option value="rejected">Rejeitada</option>
              <option value="auto_rejected">Rejeitada Auto.</option>
              <option value="recovered">Recuperada</option>
              <option value="ipss_donated">Doada IPSS</option>
              <option value="lottery">Sorteio</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notas</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Notas adicionais..." />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50">{loading ? 'A guardar...' : 'Guardar Alterações'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Approval Modal
const ApprovalModal = ({ offer, onClose, onComplete }) => {
  const [action, setAction] = useState('approve');
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Convert to number to handle cases where value might be stored as string
  const rawValue = offer.value ?? offer.estimated_value ?? 0;
  const offerValue = typeof rawValue === 'string' ? parseFloat(rawValue) || 0 : rawValue;
  const offeredBy = offer.offered_by || offer.related_entity_name || '-';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('action', action);
    formData.append('justification', justification);
    try {
      await api.post(`/offers/${offer.offer_id}/approve`, formData);
      toast.success(`Oferta ${action === 'approve' ? 'aprovada' : 'rejeitada'}`);
      onComplete();
    } catch (error) {
      toast.error('Erro ao processar oferta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Aprovar/Rejeitar Oferta</h2></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="rounded-lg bg-muted/50 p-4">
            <p className="font-medium">{offer.employee_name}</p>
            <p className="text-sm text-muted-foreground">{offerValue?.toFixed(2)} {offer.currency || 'EUR'} - {offeredBy}</p>
            {offer.direction && <p className="text-xs text-muted-foreground mt-1">Tipo: {offer.direction === 'received' ? 'Oferta Recebida' : 'Oferta Dada'}</p>}
            {offer.description && <p className="text-xs text-muted-foreground mt-1">{offer.description}</p>}
          </div>
          
          {/* Show tenant policy context */}
          {offer.tenant_settings_snapshot && (
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">Política RGPC aplicável:</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><span className="text-muted-foreground">Limite:</span> €{offer.tenant_settings_snapshot.max_offer_value}</div>
                <div><span className="text-muted-foreground">Aprovação &gt;</span> €{offer.tenant_settings_snapshot.approval_required_above}</div>
                <div><span className="text-muted-foreground">Rejeição &gt;</span> €{offer.tenant_settings_snapshot.auto_reject_above}</div>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Ação</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="radio" checked={action === 'approve'} onChange={() => setAction('approve')} className="rounded" />Aprovar</label>
              <label className="flex items-center gap-2"><input type="radio" checked={action === 'reject'} onChange={() => setAction('reject')} className="rounded" />Rejeitar</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Justificação / Motivo *</label>
            <textarea required value={justification} onChange={(e) => setJustification(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder={action === 'approve' ? 'Motivo da aprovação...' : 'Motivo da rejeição...'} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className={`flex-1 px-4 py-2 rounded-lg font-medium disabled:opacity-50 ${action === 'approve' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>{loading ? 'A processar...' : action === 'approve' ? 'Aprovar' : 'Rejeitar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Recovery Modal
const RecoveryModal = ({ offer, onClose, onComplete }) => {
  const [action, setAction] = useState('ipss_donation');
  const [justification, setJustification] = useState('');
  const [ipssEntity, setIpssEntity] = useState('');
  const [lotteryDate, setLotteryDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('action', action);
    formData.append('justification', justification);
    if (action === 'ipss_donation') formData.append('ipss_entity', ipssEntity);
    if (action === 'lottery') formData.append('lottery_date', lotteryDate);
    try {
      await api.post(`/offers/${offer.offer_id}/recover`, formData);
      toast.success('Oferta recuperada com sucesso');
      onComplete();
    } catch (error) {
      toast.error('Erro ao recuperar oferta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Recuperar Oferta</h2></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
            <p className="font-medium text-red-700 dark:text-red-400">{offer.employee_name}</p>
            <p className="text-sm text-red-600 dark:text-red-400">{offer.value?.toFixed(2)} {offer.currency || 'EUR'} - Rejeitada automaticamente</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Destino</label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="ipss_donation">Doação IPSS</option>
              <option value="lottery">Sorteio</option>
              <option value="other">Outro</option>
            </select>
          </div>
          {action === 'ipss_donation' && (
            <div>
              <label className="block text-sm font-medium mb-1">Entidade IPSS *</label>
              <input type="text" required value={ipssEntity} onChange={(e) => setIpssEntity(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Nome da IPSS..." />
            </div>
          )}
          {action === 'lottery' && (
            <div>
              <label className="block text-sm font-medium mb-1">Data do Sorteio *</label>
              <input type="date" required value={lotteryDate} onChange={(e) => setLotteryDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Justificação *</label>
            <textarea required value={justification} onChange={(e) => setJustification(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50">{loading ? 'A processar...' : 'Recuperar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Offer Detail Modal - View offer details with RGPC policy info
const OfferDetailModal = ({ offer, tenantSettings, canApprove, onClose, onApprove, onRecover }) => {
  // Get offer value (supports both value and estimated_value)
  // Convert to number to handle cases where value might be stored as string
  const rawValue = offer.value ?? offer.estimated_value ?? 0;
  const offerValue = typeof rawValue === 'string' ? parseFloat(rawValue) || 0 : rawValue;
  const offeredBy = offer.offered_by || offer.related_entity_name || '-';
  
  const getStatusInfo = () => {
    switch (offer.status) {
      case 'approved': return { color: 'bg-emerald-100 text-emerald-700', text: 'Aprovada' };
      case 'rejected': return { color: 'bg-red-100 text-red-700', text: 'Rejeitada' };
      case 'auto_rejected': return { color: 'bg-red-600 text-white', text: 'Rejeitada Automaticamente' };
      case 'pending': return { color: 'bg-amber-100 text-amber-700', text: 'Pendente' };
      case 'pending_approval': return { color: 'bg-amber-500 text-white', text: 'Aguarda Aprovação' };
      case 'ipss_donated': return { color: 'bg-purple-100 text-purple-700', text: 'Doada a IPSS' };
      case 'lottery': return { color: 'bg-indigo-100 text-indigo-700', text: 'Sorteio' };
      default: return { color: 'bg-slate-100 text-slate-700', text: offer.status };
    }
  };
  
  // Check if offer needs approval
  const needsApprovalAction = ['pending', 'pending_approval'].includes(offer.status);

  const statusInfo = getStatusInfo();
  
  // Use tenant settings from offer snapshot if available, otherwise use provided settings
  const offerSettings = offer.tenant_settings_snapshot || tenantSettings || {};
  const maxValue = offerSettings.max_offer_value || 50;
  const approvalThreshold = offerSettings.approval_required_above || offerSettings.offer_approval_required_above || 25;
  const autoRejectThreshold = offerSettings.auto_reject_above || offerSettings.auto_reject_offers_above || 500;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Detalhe da Oferta</h2>
            <p className="text-sm text-muted-foreground">ID: {offer.offer_id}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>{statusInfo.text}</span>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Main Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Colaborador</p>
              <p className="font-medium">{offer.employee_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor</p>
              <p className="text-2xl font-bold">{offerValue?.toFixed(2)} {offer.currency || 'EUR'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tipo</p>
              <p className="font-medium">
                {offerTypeLabels[offer.offer_type] || offer.offer_type}
                {offer.direction && <span className="text-xs text-muted-foreground ml-2">({offer.direction === 'received' ? 'Recebida' : 'Dada'})</span>}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Data</p>
              <p className="font-medium">{offer.offer_date || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Oferecido por / Entidade</p>
              <p className="font-medium">{offeredBy}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tipo de Entidade</p>
              <p className="font-medium capitalize">{offer.offered_by_type || offer.related_entity_type || '-'}</p>
            </div>
          </div>

          {offer.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Descrição</p>
              <p className="bg-muted/50 rounded-lg p-3">{offer.description}</p>
            </div>
          )}
          
          {offer.justification && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Justificação</p>
              <p className="bg-muted/50 rounded-lg p-3">{offer.justification}</p>
            </div>
          )}

          {/* Policy Compliance Section */}
          <div className="rounded-xl border p-4 space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Conformidade RGPC
            </h3>
            
            <div className="grid grid-cols-3 gap-3">
              <div className={`rounded-lg p-3 text-center ${offerValue <= maxValue ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200' : 'bg-red-50 dark:bg-red-900/20 border border-red-200'}`}>
                <p className="text-xs text-muted-foreground">Limite Aceitação</p>
                <p className={`font-bold ${offerValue <= maxValue ? 'text-emerald-600' : 'text-red-600'}`}>
                  {offerValue <= maxValue ? '✓' : '✗'} €{maxValue}
                </p>
              </div>
              <div className={`rounded-lg p-3 text-center ${offerValue <= approvalThreshold ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200' : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200'}`}>
                <p className="text-xs text-muted-foreground">Aprovação Requerida</p>
                <p className={`font-bold ${offerValue <= approvalThreshold ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {offerValue > approvalThreshold ? 'Sim' : 'Não'}
                </p>
              </div>
              <div className={`rounded-lg p-3 text-center ${offerValue <= autoRejectThreshold ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200' : 'bg-red-50 dark:bg-red-900/20 border border-red-200'}`}>
                <p className="text-xs text-muted-foreground">Rejeição Auto.</p>
                <p className={`font-bold ${offerValue <= autoRejectThreshold ? 'text-emerald-600' : 'text-red-600'}`}>
                  {offerValue > autoRejectThreshold ? 'Sim' : 'Não'}
                </p>
              </div>
            </div>

            {offer.policy_violation && (
              <div className="bg-red-100 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">Violação de Política:</p>
                <p className="text-sm text-red-600 dark:text-red-400">{offer.policy_violation}</p>
              </div>
            )}
            
            {offer.rejection_reason && offer.status === 'auto_rejected' && (
              <div className="bg-red-100 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">Motivo da Rejeição Automática:</p>
                <p className="text-sm text-red-600 dark:text-red-400">{offer.rejection_reason}</p>
              </div>
            )}

            {offer.validation_notes && offer.validation_notes.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium mb-1">Notas de Validação:</p>
                <ul className="text-sm space-y-1">
                  {offer.validation_notes.map((note, i) => (
                    <li key={i} className="text-muted-foreground">• {note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* RGPC Analysis if available */}
          {offer.rgpc_analysis && (
            <div className="rounded-xl border p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Análise RGPC
              </h3>
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${offer.rgpc_analysis.is_compliant ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {offer.rgpc_analysis.is_compliant ? '✓ Conforme' : '✗ Não Conforme'}
                </div>
                <span className="text-sm text-muted-foreground">Score: {(offer.rgpc_analysis.rgpc_score * 100).toFixed(0)}%</span>
              </div>
              
              {offer.rgpc_analysis.violations && offer.rgpc_analysis.violations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Violações Detetadas:</p>
                  {offer.rgpc_analysis.violations.map((v, i) => (
                    <div key={i} className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border-l-4 border-red-500">
                      <p className="font-medium text-red-700 dark:text-red-400 text-sm">{v.article}</p>
                      <p className="text-sm text-red-600 dark:text-red-400">{v.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {offer.rgpc_analysis.recommendations && offer.rgpc_analysis.recommendations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Recomendações:</p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                    {offer.rgpc_analysis.recommendations.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Approval History */}
          {(offer.approved_by || offer.rejection_reason) && (
            <div className="rounded-xl border p-4 space-y-2">
              <h3 className="font-semibold">Histórico de Aprovação</h3>
              {offer.approved_at && <p className="text-sm"><span className="text-muted-foreground">Data:</span> {new Date(offer.approved_at).toLocaleString('pt-PT')}</p>}
              {offer.approved_by && <p className="text-sm"><span className="text-muted-foreground">Por:</span> {offer.approved_by}</p>}
              {offer.rejection_reason && <p className="text-sm"><span className="text-muted-foreground">Motivo:</span> {offer.rejection_reason}</p>}
              {offer.approval_justification && <p className="text-sm"><span className="text-muted-foreground">Justificação:</span> {offer.approval_justification}</p>}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">
              Fechar
            </button>
            {needsApprovalAction && canApprove && (
              <button onClick={onApprove} className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors">
                Aprovar / Rejeitar
              </button>
            )}
            {offer.status === 'auto_rejected' && canApprove && (
              <button onClick={onRecover} className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors">
                Recuperar Oferta
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffersPage;
